import { View, TextStyle, ViewStyle } from 'react-native';
import { ReactNode } from 'react';
import Header, { HeaderProps } from '@/components/DataTable/Header';
import { ActivityIndicator } from 'react-native-paper';
import { EmptyStateProps } from '@/components/DataTable/EmptyState';
import List from '@/components/DataTable/List';
import { Flatten, SortState } from '@/utils/typeHelpers';
import { nestifyObject } from 'nestify-anything';

export type BaseDataItemType = { [K: string]: number | string | object | undefined | null };

export type HeaderItem<T> = {
  title: string;
  key: keyof Flatten<T>;
  width?: number;
  style?: ViewStyle;
  textStyle?: TextStyle;
  sortable?: boolean;
  value?: (item: T) => ReactNode;
};

export type DataTableProps<T extends BaseDataItemType> = HeaderProps<T> &
  EmptyStateProps & {
    loading: boolean;
    data?: T[];
    dataTotalCount: number;
    keyExtractor: (item: T) => string;
    headers?: (HeaderItem<T> | null)[];
    sortByState?: SortState<T>;
    onHeaderPress?: (item: HeaderItem<T>) => void;
    onItemPress?: (item: T) => void;
    subHeaderComponent?: ReactNode;
    forceListType?: 'card' | 'list';
    forceNumberOfColumns?: number;
  };

const DataTable = <T extends BaseDataItemType>(props: DataTableProps<T>) => {
  const { loading, subHeaderComponent, title } = props;
  return (
    <View style={{ flex: 1 }}>
      {title !== undefined ? <Header<T> {...props} /> : null}
      {subHeaderComponent}
      <List<T> {...props} />
    </View>
  );
};

export const getOrderByClause = <T,>(sortState: SortState<T>) =>
  nestifyObject({
    [(sortState.key as string).replace('.aggregate', '')]:
      sortState.sort === 'descending' ? 'DESC' : 'ASC',
  });

export default DataTable;
